import { User } from '@abiglobalhealth/go-types'
import { useContext } from 'react'

import { UserFormContext, UserFormContextProps, UserFormData } from '@/contexts/userForm'
import { useGeneralErrors } from '@/hooks/useGeneralErrors'

interface UseUserFormProps {
  getUserDataById: (id: string) => UserFormData
  clearData: () => void
  addData: (id: string, value: UserFormData) => void
  getUserData: () => User['data']
  clearDataById: (id: string) => void
  setUserForm: UserFormContextProps['setUserForm']
  userUpdatedFields: UserFormContextProps['userUpdatedFields']
  setUserUpdatedFields: UserFormContextProps['setUserUpdatedFields']
}

export const useUserForm = (): UseUserFormProps => {
  const { userForm, setUserForm, userUpdatedFields, setUserUpdatedFields } = useContext(UserFormContext)
  const { clearGeneralErrors } = useGeneralErrors()

  const addData = (id: string, value: UserFormData) => {
    clearGeneralErrors()

    setUserUpdatedFields((prev) => {
      const current = prev.includes(id)

      // If field is already updated or no value, just return the current list
      if (current || !value) return prev

      return [...prev, id]
    })

    setUserForm((prev) => ({
      ...prev,
      [id]: value
    }))
  }

  const clearData = () => {
    setUserForm({})
  }

  const clearDataById = (id: string) => {
    setUserForm((prev) => ({
      ...prev,
      [id]: undefined
    }))
  }

  const getUserData = (): User['data'] => {
    return userForm as User['data']
  }

  const getUserDataById = (id: string): UserFormData => {
    // Id filled in userForm, return it
    if (userForm?.[id]) return userForm[id]

    return undefined
  }

  return {
    getUserDataById,
    clearData,
    addData,
    getUserData,
    clearDataById,
    setUserForm,
    userUpdatedFields,
    setUserUpdatedFields
  }
}
